import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Typography,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import {  
    APIAppGraduateList, 
    APIAppInServiceList, 
    APIAppShortCourseList, 
    APIAppUnderGraduateArchive, 
    APIAppUnderGraduateList 
} from '../../utilities/APIs';
import CachedIcon from '@mui/icons-material/Cached';
import { TEXT_ARCHIVE, TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import { getLoggedInUserName } from '../../utilities/Functions';
import RespondentAnswersView from './RespondentAnswersView';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useAuth } from '../../users/AuthContext';
import useCustomToast from '../../hooks/useToast';

const ApplicationsViewListing = ({ applicationViewType, onCancel, titleViewName }) => {
    const { makeAuthenticatedRequest } = useAuth();    
    const [forms, setForms] = useState([]);
    const [currentView, setCurrentView] = useState("VIEWING");
    const [selectedApplicationId, setSelectedApplicationId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [formToDelete, setFormToDelete] = useState(null);
    const  showToast  = useCustomToast;

    const getApiEndpoint = () => {
        switch(applicationViewType) {
            case "GRADUATE":
                return APIAppGraduateList;
            case "UNDER":
                return APIAppUnderGraduateList;
            case "SHORT":
                return APIAppShortCourseList;
            case "INSERVICE":
                return APIAppInServiceList;
            default:
                throw new Error(`Invalid application view type: ${applicationViewType}`);
        }
    };

    const fetchForms = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const ApiCall = getApiEndpoint();
            console.log("Fetching data from:", ApiCall);
            
            const response = await makeAuthenticatedRequest(ApiCall);
            
            if (!response?.data) {
                throw new Error('No data received from server');
            }

            const data = Array.isArray(response.data) 
                ? response.data.reverse() 
                : [response.data].filter(Boolean);

            setForms(data);
            
            if (data.length === 0) {
                showToast("Info", `No applications found for ${titleViewName}`, "info");
            }
            
        } catch (err) {
            // console.error("Failed to fetch applications:", err);
            const errorMessage = err.response?.data?.message || 
                            err.message || 
                            'Failed to fetch application forms';
            setError(errorMessage);
            showToast('Error', errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (applicationViewType) {
            fetchForms();
        }
    }, [applicationViewType]);

    const handleViewAnswers = (form) => {
        setSelectedApplicationId(form.id);
        setCurrentView("ANSWERS");
    };

    const handleDeleteClick = (form) => {
        setFormToDelete(form);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!formToDelete) return;
        
        try {
            const createdBy = getLoggedInUserName();
            const formData = new FormData();
            
            if (createdBy && createdBy !== "DefaultUser") {
                formData.append('createdBy', createdBy);
            }
            formData.append('deleteId', formToDelete.id);

            const response = await makeAuthenticatedRequest(APIAppUnderGraduateArchive, {
                method: 'DELETE',
                body: formData
            });
            
            if (response?.status === "success") {
                showToast('Success', response.message || 'Form archived successfully', 'success');
                setForms(forms.filter(form => form.id !== formToDelete.id));
            } else {
                throw new Error(response?.message || 'Failed to archive form');
            }
            
        } catch (err) {
            console.error("Archive error:", err);
            const errorMsg = err.message.includes('Network') 
                ? `${err.message} ${TEXT_CONNECTION_ERROR}`
                : err.message || 'Failed to archive form';
            showToast('Error', errorMsg, 'error');
        } finally {
            setDeleteConfirmOpen(false);
            setFormToDelete(null);
        }
    };

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt={4}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading {titleViewName}...</Typography>
            </Box>
        );
    }

    if (error) {
        showToast('Error',error,'error')
        return; 
    }

    return (
        <Box p={3}>
            {currentView === "VIEWING" && (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Button 
                            variant="contained" 
                            color='secondary'
                            startIcon={<CachedIcon />}
                            onClick={fetchForms}
                            sx={{ minWidth: 150 }}
                        >
                            Refresh
                        </Button>
                        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                            {titleViewName}
                        </Typography>
                        <Button 
                            variant="contained" 
                            color='error'
                            startIcon={<ArrowBackIosIcon />}
                            onClick={onCancel}
                            sx={{ minWidth: 150 }}
                        >
                            Back 
                        </Button>
                    </Box>
                    
                    <TableContainer component={Paper} elevation={3}>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell><b>Submitted By</b></TableCell>
                                    <TableCell><b>Form Name</b></TableCell>
                                    <TableCell><b>Status</b></TableCell>
                                    <TableCell><b>Created At</b></TableCell>
                                    <TableCell><b>Actions</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {forms.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography variant="h6" color="textSecondary">
                                                No applications found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    forms.map((form) => (
                                        <TableRow key={form.id} hover>
                                            <TableCell>{form.submittedBy || 'Unknown'}</TableCell>
                                            <TableCell>{form.formName || 'Untitled Form'}</TableCell>
                                            <TableCell>
                                                <StatusBadge status={form.applicationStatus} />
                                            </TableCell>
                                            <TableCell>
                                                {form.createdAt ? new Date(form.createdAt).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" gap={1}>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => handleViewAnswers(form)}
                                                        size="small"
                                                        startIcon={<VisibilityIcon />}
                                                    >
                                                        View
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(form)}
                                                        size="small"
                                                        startIcon={<DeleteIcon />}
                                                    >
                                                        Archive
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {currentView === "ANSWERS" && selectedApplicationId && (
                <RespondentAnswersView
                    applicationId={selectedApplicationId}
                    applicationType={applicationViewType}
                    onBack={() => setCurrentView("VIEWING")}
                />
            )}

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Confirm Archive</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to archive this application? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={confirmDelete} 
                        color="error"
                        variant="contained"
                        startIcon={<DeleteIcon />}
                    >
                        Archive
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const StatusBadge = ({ status }) => {
    const statusConfig = {
        PENDING: { bgColor: '#fff3cd', textColor: '#856404', label: 'Pending' },
        ADMITTED: { bgColor: '#d1edff', textColor: '#0c5460', label: 'Admitted' },
        REJECTED: { bgColor: '#f8d7da', textColor: '#721c24', label: 'Rejected' },
        default: { bgColor: '#e2e3e5', textColor: '#495057', label: 'Unknown' }
    };

    const config = statusConfig[status] || statusConfig.default;

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                backgroundColor: config.bgColor,
                color: config.textColor
            }}
        >
            {config.label}
        </Box>
    );
};

export default ApplicationsViewListing;